// components/useGridGenerationForm.ts
import React, { useCallback, useMemo, useState } from 'react';
import type { KernelHandle, Rect } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { enforceStudioModelMinimumGenerations, estimateStudioModelCredits } from '#lib/model-registry';
import { isNanoBanana2OrNewer } from '#lib/nanoBanana';
import {
  DEFAULT_GRID_GENERATION_RUNTIME,
  GRID_SIZE_MAX,
  GRID_SIZE_MIN,
  GridGenerationPayload,
  GridEdgeFadeRadius,
  GridGenerationRuntime,
  GridHarmonizationMode,
  GridOrder,
  GridTileShape,
  GridSweepMode,
  buildGridPreviewZones,
  buildGridScript,
  computeGridFrameCount,
  normalizeGridEdgeFadeRadius,
  normalizeGridHarmonizationMode,
  normalizeGridOverlapScale,
  normalizeGridTileShape,
  parseAndValidateGridSize,
  resolveDefaultGridEdgeFadeRadius,
  resolveDefaultGridHarmonizationMode,
  resolveDefaultGridPreserveHighRes,
  resolveDefaultGridSizeFromCoverage,
  resolveGridGenerationModelId,
} from '#lib/grid-generation-script';

/**
 * Shared grid-generation form state and script orchestration for the inline panel.
 * Surface-specific labels stay local to their own component.
 */
export const gridGenerationSharedTranslations = {
  en: {
    title: 'Grid Generation',
    gridSize: 'Grid size',
    sweepMode: 'Sweep mode',
    classicGridSweep: 'Classic grid sweep',
    overlapSweepFromCenter: 'Overlap sweep from center',
    tileOverlap: 'Tile overlap',
    tileHarmonization: 'Harmonize each tile',
    noHarmonization: 'No harmonization',
    softHarmonization: 'Soft',
    strictHarmonization: 'Strict',
    preserveHighRes: 'Preserve existing high-resolution pixels',
    tileEdgeFade: 'Tile edge fade',
    tileShape: 'Tile shape',
    frameTileShape: 'Match generation area',
    squareTileShape: 'Square only',
    noEdgeFade: 'No edge fade',
    autoEdgeFade: 'Match tile overlap (auto)',
    edgeFadePixels: (pixels: number) => `${pixels}px`,
    noOverlapHint: '100% = no overlap',
    sweepOrder: 'Sweep order',
    centerOut: 'Center out',
    rowMajor: 'Row major',
    centerProbeEven: 'Center probe for even grids',
    on: 'On',
    off: 'Off',
    tiles: 'Tiles',
    credits: 'Credits',
    creditsUnavailable: 'Cost unavailable',
    cancel: 'Cancel',
    buildAndRun: 'Build & Run',
    gridSizeError: `Grid size must be an integer between ${GRID_SIZE_MIN} and ${GRID_SIZE_MAX}.`,
    missingProjectId: 'Project ID is required to run grid generation.',
    activeWorkspaceNotFound: 'Active workspace not found.',
    invalidBaseRect: 'Cannot build grid script: invalid base rectangle.',
    scriptGenerated: (count: number) => `Generated GA grid script (${count} commands).`,
    runFailed: 'Failed to execute GA grid script.',
    generateOrRunFailed: 'Failed to generate or execute GA grid script.',
  },
  fr: {
    title: 'Generation en grille',
    gridSize: 'Taille de grille',
    sweepMode: 'Mode de balayage',
    classicGridSweep: 'Balayage grille classique',
    overlapSweepFromCenter: 'Balayage overlap depuis le centre',
    tileOverlap: 'Recouvrement des tuiles',
    tileHarmonization: 'Harmoniser chaque tuile',
    noHarmonization: 'Sans harmonisation',
    softHarmonization: 'Douce',
    strictHarmonization: 'Stricte',
    preserveHighRes: 'Conserver les pixels existants en haute resolution',
    tileEdgeFade: 'Fondu des bords de tuile',
    tileShape: 'Forme des tuiles',
    frameTileShape: 'Respecter la zone de generation',
    squareTileShape: 'Carres uniquement',
    noEdgeFade: 'Aucun fondu',
    autoEdgeFade: 'Suivre le recouvrement de tuile (auto)',
    edgeFadePixels: (pixels: number) => `${pixels}px`,
    noOverlapHint: '100% = aucun recouvrement',
    sweepOrder: 'Ordre de balayage',
    centerOut: 'Du centre vers l exterieur',
    rowMajor: 'Ligne par ligne',
    centerProbeEven: 'Sonde centrale pour grilles paires',
    on: 'On',
    off: 'Off',
    tiles: 'Tuiles',
    credits: 'Credits',
    creditsUnavailable: 'Cout indisponible',
    cancel: 'Annuler',
    buildAndRun: 'Construire et lancer',
    gridSizeError: `La taille de grille doit etre un entier entre ${GRID_SIZE_MIN} et ${GRID_SIZE_MAX}.`,
    missingProjectId: 'ID projet requis pour lancer la generation en grille.',
    activeWorkspaceNotFound: 'Workspace actif introuvable.',
    invalidBaseRect: 'Impossible de creer le script: rectangle de base invalide.',
    scriptGenerated: (count: number) => `Script GA genere (${count} commandes).`,
    runFailed: 'Echec de l execution du script GA.',
    generateOrRunFailed: 'Echec de generation ou d execution du script GA.',
  },
} as const;

interface UseGridGenerationFormOptions {
  projectId: string;
  /** Workspace whose rects feed the defaults and the preview. */
  workspaceId: string;
  payload?: GridGenerationPayload;
  /** Resolves the workspace the script runs on, or throws a localized error. */
  resolveWorkspaceId: (kernelHandle: KernelHandle, projectId: string) => string;
  /** Surface label appended to the user action name. */
  logActionName: string;
  onClose: () => void;
  /** Called whenever a form control changes (the inline panel resets its tile preview). */
  onControlChange?: () => void;
}

/**
 * State, derived values and submit orchestration shared by the grid generation
 * inline panel and modal. Only the workspace resolution, the log label and the
 * control-change hook differ between the two surfaces.
 */
export function useGridGenerationForm({
  projectId,
  workspaceId,
  payload,
  resolveWorkspaceId,
  logActionName,
  onClose,
  onControlChange,
}: UseGridGenerationFormOptions) {
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const t = useMemo(
    () =>
      gridGenerationSharedTranslations[lang as keyof typeof gridGenerationSharedTranslations]
      ?? gridGenerationSharedTranslations.en,
    [lang]
  );
  const currentModelId = useKernelStore((state) => state.diffusionModel);
  const modelParams = useKernelStore((state) => state.modelParams);
  const organisationId = useKernelStore((state) => state.userContext.organisationId);
  const localNumOutputs = useKernelStore((state) => state.inventory.Project.registry[projectId.trim()]?.runtime?.localNumOutputs);
  const generationRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.generationRect ?? null
  );
  const frameRect = useKernelStore(
    (state) => state.inventory.StudioWorkspace.registry[workspaceId]?.frameRect ?? null
  );
  const defaultGridSize = useMemo(
    () => resolveDefaultGridSizeFromCoverage(generationRect, frameRect),
    [generationRect, frameRect]
  );
  const initialRuntime = useMemo<GridGenerationRuntime>(() => ({
    ...DEFAULT_GRID_GENERATION_RUNTIME,
    ...(payload?.runtime ?? {}),
    gridSize: (() => {
      const rawGridSize = (payload?.runtime ?? {}).gridSize;
      if (typeof rawGridSize === 'number' && Number.isFinite(rawGridSize)) {
        const normalized = Math.round(rawGridSize);
        if (normalized >= GRID_SIZE_MIN && normalized <= GRID_SIZE_MAX) {
          return normalized;
        }
      }
      return defaultGridSize;
    })(),
    overlapScale: normalizeGridOverlapScale(
      (payload?.runtime ?? {}).overlapScale ?? DEFAULT_GRID_GENERATION_RUNTIME.overlapScale
    ),
    tileHarmonization: resolveDefaultGridHarmonizationMode(
      payload?.modelPreset,
      (payload?.runtime ?? {}).tileHarmonization
    ),
    preserveHighRes: resolveDefaultGridPreserveHighRes(
      payload?.modelPreset,
      (payload?.runtime ?? {}).preserveHighRes
    ),
    tileEdgeFadeRadius: resolveDefaultGridEdgeFadeRadius(
      payload?.modelPreset,
      (payload?.runtime ?? {}).tileEdgeFadeRadius
    ),
    tileShape: normalizeGridTileShape((payload?.runtime ?? {}).tileShape),
  }), [payload, defaultGridSize]);

  const [gridSizeInput, setGridSizeInputState] = useState(() => String(initialRuntime.gridSize));
  const [includeCenterProbeForEven, setIncludeCenterProbeForEvenState] = useState(
    () => initialRuntime.includeCenterProbeForEven
  );
  const [overlapScale, setOverlapScaleState] = useState(
    () => normalizeGridOverlapScale(initialRuntime.overlapScale)
  );
  const [tileHarmonization, setTileHarmonizationState] = useState(
    () => normalizeGridHarmonizationMode(initialRuntime.tileHarmonization)
  );
  const [preserveHighRes, setPreserveHighResState] = useState(
    () => initialRuntime.preserveHighRes
  );
  const [tileEdgeFadeRadius, setTileEdgeFadeRadiusState] = useState<GridEdgeFadeRadius>(
    () => normalizeGridEdgeFadeRadius(initialRuntime.tileEdgeFadeRadius)
  );
  const [tileShape, setTileShapeState] = useState<GridTileShape>(
    () => normalizeGridTileShape(initialRuntime.tileShape)
  );
  const [order, setOrderState] = useState<GridOrder>(() => initialRuntime.order);
  const [sweepMode, setSweepModeState] = useState<GridSweepMode>(() => initialRuntime.sweepMode);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setGridSizeInput = useCallback((value: string) => {
    setGridSizeInputState(value);
    onControlChange?.();
  }, [onControlChange]);
  const setIncludeCenterProbeForEven = useCallback((value: boolean) => {
    setIncludeCenterProbeForEvenState(value);
    onControlChange?.();
  }, [onControlChange]);
  const setOverlapScale = useCallback((value: number) => {
    setOverlapScaleState(normalizeGridOverlapScale(value));
    onControlChange?.();
  }, [onControlChange]);
  const setTileHarmonization = useCallback((value: GridHarmonizationMode) => {
    setTileHarmonizationState(normalizeGridHarmonizationMode(value));
    onControlChange?.();
  }, [onControlChange]);
  const setPreserveHighRes = useCallback((value: boolean) => {
    setPreserveHighResState(value);
    onControlChange?.();
  }, [onControlChange]);
  const setTileEdgeFadeRadius = useCallback((value: GridEdgeFadeRadius) => {
    setTileEdgeFadeRadiusState(normalizeGridEdgeFadeRadius(value));
    onControlChange?.();
  }, [onControlChange]);
  const setTileShape = useCallback((value: GridTileShape) => {
    setTileShapeState(normalizeGridTileShape(value));
    onControlChange?.();
  }, [onControlChange]);
  const setOrder = useCallback((value: GridOrder) => {
    setOrderState(value);
    onControlChange?.();
  }, [onControlChange]);
  const setSweepMode = useCallback((value: GridSweepMode) => {
    setSweepModeState(value);
    onControlChange?.();
  }, [onControlChange]);

  const overlapPercent = useMemo(() => Math.round(overlapScale * 100), [overlapScale]);
  const generationModelId = useMemo(() => (
    resolveGridGenerationModelId(payload, currentModelId)
  ), [payload, currentModelId]);
  const gridMetrics = useMemo(() => {
    const gridSize = parseAndValidateGridSize(gridSizeInput);
    if (gridSize === null) return null;
    const previewRuntime: GridGenerationRuntime = {
      ...DEFAULT_GRID_GENERATION_RUNTIME,
      gridSize,
      includeCenterProbeForEven,
      order,
      sweepMode,
      overlapScale,
      tileHarmonization,
      preserveHighRes,
      tileEdgeFadeRadius,
      tileShape,
    };
    const frameCount = generationRect
      ? buildGridPreviewZones(generationRect, previewRuntime, frameRect).length
      : computeGridFrameCount(gridSize, includeCenterProbeForEven, sweepMode);
    const estimatedPerTileCredits = estimateStudioModelCredits(
      generationModelId,
      (modelParams[generationModelId] as Record<string, unknown> | null | undefined) ?? null,
      { organisationId },
    );
    const outputsPerTile = isNanoBanana2OrNewer(generationModelId)
      ? enforceStudioModelMinimumGenerations(generationModelId, localNumOutputs)
      : 1;
    return {
      frameCount,
      estimatedCredits: estimatedPerTileCredits === null
        ? null
        : frameCount * estimatedPerTileCredits * outputsPerTile,
    };
  }, [
    generationModelId,
    generationRect,
    gridSizeInput,
    includeCenterProbeForEven,
    modelParams,
    localNumOutputs,
    organisationId,
    frameRect,
    order,
    overlapScale,
    preserveHighRes,
    sweepMode,
    tileEdgeFadeRadius,
    tileHarmonization,
    tileShape,
  ]);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const kernelHandle = kernel.current;
      if (!kernelHandle) return;

      setError(null);
      const gridSize = parseAndValidateGridSize(gridSizeInput);
      if (gridSize === null) {
        setError(t.gridSizeError);
        return;
      }

      const runtime: GridGenerationRuntime = {
        gridSize,
        includeCenterProbeForEven,
        clampToFrame: true,
        order,
        sweepMode,
        overlapScale: normalizeGridOverlapScale(overlapScale),
        tileHarmonization: normalizeGridHarmonizationMode(tileHarmonization),
        preserveHighRes,
        tileEdgeFadeRadius: normalizeGridEdgeFadeRadius(tileEdgeFadeRadius),
        tileShape: normalizeGridTileShape(tileShape),
      };

      setIsSubmitting(true);
      try {
        const normalizedProjectId = projectId.trim();
        if (!normalizedProjectId) {
          throw new Error(t.missingProjectId);
        }
        const targetWorkspaceId = resolveWorkspaceId(kernelHandle, normalizedProjectId);
        const workspace = kernelHandle.getWorkspace(targetWorkspaceId);
        if (!workspace) {
          throw new Error(t.activeWorkspaceNotFound);
        }

        const baseRect: Rect = {
          x: workspace.generationRect.x,
          y: workspace.generationRect.y,
          w: workspace.generationRect.w,
          h: workspace.generationRect.h,
        };

        if (!(baseRect.w > 0 && baseRect.h > 0)) {
          throw new Error(t.invalidBaseRect);
        }

        const batchId = crypto.randomUUID();
        const script = buildGridScript(
          baseRect,
          runtime,
          generationModelId,
          payload?.modelPreset,
          {
            batchId,
            modelParams: (kernelHandle.getModelParams()[generationModelId] as Record<string, unknown> | undefined) ?? null,
          }
        );

        await kernelHandle.addAssistantMessage(
          t.scriptGenerated(script.commands.length),
          [script],
          'Fater'
        );
        kernelHandle.logUserAction(logActionName, {
          workspaceId: targetWorkspaceId,
          gridSize,
          modelId: generationModelId,
          source: payload?.source || 'unknown',
          order,
          sweepMode,
          includeCenterProbeForEven,
          clampToFrame: true,
          overlapScale,
          tileHarmonization,
          preserveHighRes,
          tileEdgeFadeRadius,
          tileShape,
        });

        onClose();

        void kernelHandle
          .runGridBatch(targetWorkspaceId, script, batchId)
          .catch((runError) => {
            const message =
              runError instanceof Error
                ? runError.message
                : t.runFailed;
            console.error('GA grid script execution failed', runError);
            kernelHandle.handleError(message);
          });
      } catch (submitError) {
        const message =
          submitError instanceof Error
            ? submitError.message
            : t.generateOrRunFailed;
        setError(message);
        kernelHandle.handleError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      kernel,
      gridSizeInput,
      includeCenterProbeForEven,
      overlapScale,
      tileHarmonization,
      preserveHighRes,
      tileEdgeFadeRadius,
      tileShape,
      order,
      sweepMode,
      generationModelId,
      payload?.modelPreset,
      payload?.source,
      onClose,
      projectId,
      resolveWorkspaceId,
      logActionName,
      t,
    ]
  );

  return {
    t,
    generationRect,
    frameRect,
    gridSizeInput,
    setGridSizeInput,
    includeCenterProbeForEven,
    setIncludeCenterProbeForEven,
    overlapScale,
    setOverlapScale,
    tileHarmonization,
    setTileHarmonization,
    preserveHighRes,
    setPreserveHighRes,
    tileEdgeFadeRadius,
    setTileEdgeFadeRadius,
    tileShape,
    setTileShape,
    order,
    setOrder,
    sweepMode,
    setSweepMode,
    error,
    isSubmitting,
    gridMetrics,
    overlapPercent,
    generationModelId,
    handleSubmit,
  };
}
